import { useState } from "react";
import { AlertTriangle, Check, Loader2, PencilLine, RotateCcw, X } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import type { AronPendingWrite } from "@/features/aron/types";
import {
  ChainResultNote,
  chainOptionFor,
  useWriteBodyEdit,
  WriteEntityLink,
  WritePlannedSteps,
  WriteStepResults,
} from "@/features/aron/components/AronWriteProposal";
import { writeStepsError } from "@/features/aron/types";
import {
  useCeoExecuteEmailWrite,
  useCeoRejectEmailWrite,
  useCeoRetryEmailWriteSteps,
} from "../api/aronMailApi";

const METHOD_VERB: Record<string, string> = {
  POST: "Utworzenie",
  PATCH: "Aktualizacja",
  PUT: "Aktualizacja",
};

const STATUS_BADGE: Record<AronPendingWrite["status"], { label: string; cls: string }> = {
  pending: { label: "Czeka na zatwierdzenie", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Zatwierdzanie…", cls: "bg-amber-100 text-amber-800" },
  executed: { label: "Wykonano", cls: "bg-green-100 text-green-800" },
  failed: { label: "Błąd wykonania", cls: "bg-red-100 text-red-800" },
  rejected: { label: "Odrzucono", cls: "bg-gray-100 text-gray-600" },
  withdrawn: { label: "Wycofano (ARON)", cls: "bg-gray-100 text-gray-600" },
};

/**
 * CEO on-behalf variant of the write gate card (see AronWriteProposal for the
 * owner path). Execution runs under the CEO's identity — the label says so.
 */
export function AronMailWriteCard({
  emailId,
  ownerName,
  write,
}: {
  emailId: string;
  ownerName: string;
  write: AronPendingWrite;
}) {
  const [chainChecked, setChainChecked] = useState(false);
  const execute = useCeoExecuteEmailWrite();
  const reject = useCeoRejectEmailWrite();
  const busy = execute.isPending || reject.isPending;
  const pending = write.status === "pending";
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  const bodyEdit = useWriteBodyEdit(write, isDeveloper && pending);
  const retry = useCeoRetryEmailWriteSteps();
  // Main write done, a dependent step not — the badge must not read as success.
  const stepsError = writeStepsError(write);
  const badge = stepsError
    ? { label: "Wykonano częściowo", cls: "bg-amber-100 text-amber-800" }
    : STATUS_BADGE[write.status];
  const stepCount = write.follow_ups?.length ?? 0;
  const chainOption = stepCount ? null : chainOptionFor(write);

  const failedReason =
    write.status === "failed"
      ? String(
          write.response_summary?.error_detail ||
            write.response_summary?.denied_reason ||
            "",
        ) || `Kod odpowiedzi ${write.response_status ?? "?"}`
      : "";

  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/20">
      <div className="flex items-start gap-2 px-3 py-2">
        <PencilLine size={15} className="mt-0.5 shrink-0 text-red-600" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {METHOD_VERB[write.method] || write.method} danych
            </span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          {write.description && (
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
              {write.description}
            </p>
          )}
          <p className="mt-0.5 text-xs text-gray-500">
            Propozycja z e-maila użytkownika {ownerName || "—"}. Zatwierdzenie
            wykona zmianę na Twoich uprawnieniach (CEO).
          </p>
          {bodyEdit.tableNode}
          {pending && <WritePlannedSteps write={write} />}
          {bodyEdit.rawNode}
          {failedReason && (
            <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle size={12} /> {failedReason}
            </p>
          )}
          <WriteStepResults write={write} />
          {stepsError && (
            <div className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 dark:border-amber-900/60 dark:bg-amber-950/30">
              <p className="flex items-start gap-1 text-xs text-amber-800 dark:text-amber-200">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {stepsError}
              </p>
              <button
                type="button"
                disabled={retry.isPending}
                onClick={() => retry.mutate({ writeId: write.id, emailId })}
                className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {retry.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RotateCcw size={12} />
                )}
                Ponów nieudane kroki
              </button>
            </div>
          )}
          <ChainResultNote write={write} />
          {write.status === "executed" && <WriteEntityLink write={write} />}
        </div>
      </div>

      {pending && (
        <div className="border-t border-red-200/70 px-3 py-2 dark:border-red-900/40">
          {chainOption && (
            <label className="mb-2 flex cursor-pointer items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={chainChecked}
                onChange={(e) => setChainChecked(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              {chainOption.label}
            </label>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => reject.mutate({ writeId: write.id, emailId })}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {reject.isPending ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
              Odrzuć
            </button>
            <button
              type="button"
              disabled={busy || !bodyEdit.valid}
              title={!bodyEdit.valid ? "Popraw JSON, aby zatwierdzić" : undefined}
              onClick={() =>
                execute.mutate({
                  writeId: write.id,
                  emailId,
                  body: bodyEdit.body,
                  followUps: bodyEdit.followUps,
                  chainAction:
                    chainOption && chainChecked ? chainOption.action : undefined,
                })
              }
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {execute.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {stepCount
                ? `Zatwierdź i wykonaj ${stepCount + 1} kroki (jako CEO)`
                : "Zatwierdź i wykonaj (jako CEO)"}
              {bodyEdit.dirty && " (edytowane)"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
